import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';

import {
  AlertTriggerType,
  triggerTypeIconPaths,
} from 'xm-constants/triggerTypes';
import { scriptStepTypes } from '@xmatters/common/constants/scriptSteps';
import {
  ALERT_INTEGRATION_DATA_PROP,
  CreateAlertStepInputNames,
} from 'xm-constants/toolStep';
import { getDefaultFlowTriggerIntegrationData } from 'utils/flow';

import {
  deserializeCreateAlertResponseOptions,
  deserializeCreateAlertTriggers,
} from 'utils/flowBlockInput';
import { integrationHasChanges } from 'utils/integrations';
import {
  IDEAL_NAME_KEY,
  getIdealIntegrationName,
  syncSubTriggerBlocksByMutatingCanvas,
} from './subTriggerUtils';

// Create Alert doesn't support as many changes:
const MODIFIABLE_INTEGRATION_FIELDS = ['name', 'deployed'];

/**
 * getCreateAlertIntegrationUpdates determines integration POST requests needed. There are a few of these:
 *   1. Subtrigger added, so we need to create a new integration
 *   2. Block label has changed, or response value has changed, so we need to update the integration name
 *   3. Integration was disabled/enabled
 *
 * Attempts to generate the prettiest names possible, preferably of form:
 *   `$BLOCK_LABEL $TRIGGER_TYPE` (followed by `- $RESPONSE_TEXT` for response triggers)
 * This may not always be possible, e.g. if the user named integrations this way already, in which case we
 * add to the end to make it unique.
 */
export const getCreateAlertIntegrationUpdates = (blocks, allIntegrations) => {
  const integrationsById = keyBy(allIntegrations, 'id');
  const integrationNamesToIdSets = reduce(
    allIntegrations,
    (acc, integration) => {
      if (!acc[integration.name]) {
        acc[integration.name] = new Set();
      }
      acc[integration.name].add(integration.id);
      return acc;
    },
    {},
  );

  return (
    blocks
      .map(block => get(block, ['step', ALERT_INTEGRATION_DATA_PROP]))
      // Filter out items that do not have the alert data prop, as well as those that do but have no id (i.e. RESPONSES parent)
      .filter(itemData => itemData?.id)
      // Set the nicest names we can:
      .map(integration => {
        const { id } = integration;
        let name = integration[IDEAL_NAME_KEY] || integration.name;
        const idsForName = integrationNamesToIdSets[name];
        let shouldRecordNewName = false;
        if (!idsForName || (idsForName.size === 1 && idsForName.has(id))) {
          // Either no integration has this name yet, or there's only one and it's this
          shouldRecordNewName = !idsForName;
        } else {
          shouldRecordNewName = true;
          // Name has a collision, add a unique identifier:
          name = `${name} - ${id}`; // TODO: this is better than it was before, but a plain number would still be cleaner
        }
        if (shouldRecordNewName) {
          integrationNamesToIdSets[name] = new Set();
          integrationNamesToIdSets[name].add(id);
        }
        return {
          ...integration,
          name,
        };
      })
      // Identity fn to only keep items that had ALERT_INTEGRATION_DATA_PROP:
      .filter(item =>
        integrationHasChanges(
          item,
          integrationsById[item.id],
          MODIFIABLE_INTEGRATION_FIELDS,
        ),
      )
      .map(item => {
        // If the integration already exists, POST only the modifiable fields (as xm-api throws a 409 otherwise):
        if (integrationsById[item.id]) {
          return {
            id: item.id,
            ...pick(item, MODIFIABLE_INTEGRATION_FIELDS),
          };
        }
        return item;
      })
  );
};

/**
 * Pre-render data syncing methods:
 */

function getNewCreateAlertBlockStepData(triggerPort, block, planId, prevStep) {
  const { triggerType } = triggerPort;
  if (triggerType === AlertTriggerType.RESPONSE) {
    return { type: scriptStepTypes.RESPONSES };
  }

  const idealName = getIdealIntegrationName(block, triggerPort);
  const integrationData = getDefaultFlowTriggerIntegrationData({
    type: scriptStepTypes.OUTBOUND_WEBHOOK,
    planId,
    uniqueName: idealName,
    id: prevStep?.id || triggerPort.data?.integrationId,
  });
  const isResponseOption = triggerType === AlertTriggerType.RESPONSEOPTION;
  const triggeredBy = isResponseOption
    ? AlertTriggerType.RESPONSE
    : triggerType;

  integrationData.triggeredBy = triggeredBy;
  integrationData.deployed = !triggerPort.disabled;
  integrationData[IDEAL_NAME_KEY] = idealName;

  return {
    id: integrationData.id,
    icon: triggerTypeIconPaths[triggeredBy],
    type: integrationData.type,
    triggeredBy,
    [ALERT_INTEGRATION_DATA_PROP]: integrationData,
  };
}

const makePort = trigger => ({
  ...trigger,
  data: trigger,
  jsPlumbId: trigger.triggerBlockId, // Used in jsplumb
  portType: 'pseudoBlockOutput',
});

const makeAlertResponsePort = response => ({
  ...makePort(response),
  triggerType: AlertTriggerType.RESPONSEOPTION,
});

export const getCreateAlertBlocks = blocks =>
  filter(
    blocks,
    ({ step }) => step && step.type === scriptStepTypes.CREATE_ALERT,
  );

function getSortedNotifyTriggerPorts(triggers, responseOptions) {
  const visibleTriggers = reject(triggers, 'hidden').filter(
    ({ triggerBlockId }) => !!triggerBlockId,
  );
  const sortedTriggers = sortBy(visibleTriggers, 'position');
  const ports = [];

  forEach(sortedTriggers, port => {
    ports.push(makePort(port));
    if (port.triggerType === AlertTriggerType.RESPONSE) {
      forEach(responseOptions, response => {
        ports.push(makeAlertResponsePort(response));
      });
    }
  });

  return ports;
}

const getInputByStepInputName = (inputs, blockId, name) =>
  inputs.find(
    input =>
      input.flowBlock.id === blockId && input.scriptStepInput?.name === name,
  );

export const injectCreateAlertTriggers = (selectedCanvas, planId) => {
  const { TRIGGERS, RESPONSES } = CreateAlertStepInputNames;
  const { blocks, inputs } = selectedCanvas;
  const createAlertBlocks = getCreateAlertBlocks(blocks);
  const triggerIdToBlockIdMap = {};

  createAlertBlocks.forEach(block => {
    const { id } = block;

    const triggersInput = getInputByStepInputName(inputs, id, TRIGGERS);
    const triggers = deserializeCreateAlertTriggers(triggersInput?.value);

    const responseOptionsInput = getInputByStepInputName(inputs, id, RESPONSES);
    const responseOptions = deserializeCreateAlertResponseOptions(
      responseOptionsInput?.value,
    );

    const notifyTriggerPorts = getSortedNotifyTriggerPorts(
      triggers,
      responseOptions,
    );

    forEach(notifyTriggerPorts, trigger => {
      triggerIdToBlockIdMap[trigger.triggerBlockId] = id;
    });

    /* eslint-disable no-param-reassign */
    block.ports = notifyTriggerPorts;
    syncSubTriggerBlocksByMutatingCanvas({
      block,
      getStepDataFunction: getNewCreateAlertBlockStepData,
      planId,
      selectedCanvas,
      triggerPorts: notifyTriggerPorts,
    });
    /* eslint-enable no-param-reassign */
  });
};
